/**
 * Обкладинки деяких творів (smakolykytl: bucket.smakolykytl.site/projects/…)
 * приходять із джерела з "подвійно закодованими" іменами файлів: кирилиця в
 * імені вже перетворена на mojibake (UTF-8 байти прочитали як Latin-1/cp1252 і знову
 * закодували), тож браузер просить `%C3%90%C2%92…` замість `%D0%92…` і
 * отримує помилку. Що з цим робимо:
 *
 *  1. Для адреси будуємо запасні варіанти (imageUrlCandidates): "виправлений"
 *     (mojibake → нормальна кирилиця) і, на випадок, якщо файл насправді
 *     збережено під зіпсованим іменем, "зіпсовані" дзеркала (Latin-1 та cp1252).
 *  2. <img>, що не завантажилась, перебирає ці варіанти по черзі
 *     (installImageFallback — один глобальний обробник помилок).
 *  3. Фонові картинки (CSS background — у них немає події помилки) перевіряються
 *     через new Image() (resolveWorkingImageUrl / setHeroBackground).
 */

const MOJIBAKE_RE = /[\u00C2-\u00F4][\u0080-\u00BF\u0152\u0153\u0160\u0161\u0178\u017D\u017E\u0192\u02C6\u02DC\u2013\u2014\u2018-\u201E\u2020-\u2022\u2026\u2030\u2039\u203A\u20AC\u2122]/;

/** cp1252: байти 0x80–0x9F → символи Юнікоду (решта збігається з Latin-1). 0 — байт не визначений у cp1252. */
const CP1252_HIGH = [
  0x20ac, 0, 0x201a, 0x0192, 0x201e, 0x2026, 0x2020, 0x2021, 0x02c6, 0x2030, 0x0160, 0x2039, 0x0152, 0, 0x017d, 0,
  0, 0x2018, 0x2019, 0x201c, 0x201d, 0x2022, 0x2013, 0x2014, 0x02dc, 0x2122, 0x0161, 0x203a, 0x0153, 0, 0x017e, 0x0178,
];
const CP1252_REVERSE = new Map<number, number>();
CP1252_HIGH.forEach((cp, i) => cp && CP1252_REVERSE.set(cp, 0x80 + i));

function safeDecode(s: string): string | null {
  try {
    return decodeURIComponent(s);
  } catch {
    return null;
  }
}

/** mojibake (Latin-1 чи cp1252) → нормальний текст; null, якщо сегмент не схожий на mojibake або не відновлюється. */
function repairSegment(seg: string): string | null {
  if (!MOJIBAKE_RE.test(seg)) return null;
  const bytes = new Uint8Array(seg.length);
  for (let i = 0; i < seg.length; i++) {
    const c = seg.charCodeAt(i);
    if (c <= 0xff) bytes[i] = c;
    else if (CP1252_REVERSE.has(c)) bytes[i] = CP1252_REVERSE.get(c)!;
    else return null;
  }
  try {
    const fixed = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    return fixed !== seg ? fixed : null;
  } catch {
    return null;
  }
}

/** нормальний текст → mojibake; cp1252=false — через Latin-1 (керуючі символи U+0080–U+009F лишаються), true — через cp1252. null, якщо в сегменті лише ASCII. */
function garbleSegment(seg: string, cp1252: boolean): string | null {
  if (!/[^\u0000-\u007F]/.test(seg)) return null;
  let out = '';
  for (const b of new TextEncoder().encode(seg)) {
    out += String.fromCharCode(cp1252 && b >= 0x80 && b <= 0x9f && CP1252_HIGH[b - 0x80] ? CP1252_HIGH[b - 0x80] : b);
  }
  return out;
}

function mapPath(url: URL, fn: (seg: string) => string | null): string | null {
  let changed = false;
  const segs = url.pathname.split('/').map((raw) => {
    const decoded = safeDecode(raw);
    if (decoded === null) return raw;
    const next = fn(decoded);
    if (next === null) return raw;
    changed = true;
    return encodeURIComponent(next);
  });
  if (!changed) return null;
  const copy = new URL(url.href);
  copy.pathname = segs.join('/');
  return copy.href;
}

/** Запасні адреси для картинки (без дублікатів і без самої адреси), у порядку спроб. Порожньо — міняти нічого. */
export function imageUrlCandidates(src: string): string[] {
  let url: URL;
  try {
    url = new URL(src, window.location.href);
  } catch {
    return [];
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return [];
  const list = [
    mapPath(url, repairSegment),
    mapPath(url, (s) => garbleSegment(s, false)),
    mapPath(url, (s) => garbleSegment(s, true)),
  ];
  const seen = new Set<string>([url.href]);
  return list.filter((u): u is string => {
    if (!u || seen.has(u)) return false;
    seen.add(u);
    return true;
  });
}

/** Перший запасний варіант (або null). Лишено для сумісності. */
export function alternativeImageUrl(src: string): string | null {
  return imageUrlCandidates(src)[0] ?? null;
}

/** Чи <img> зараз пробує запасну адресу — власний обробник error картинки (напр. в coverEl) у цей момент не повинен її прибирати. */
export function hasPendingImageAlternative(img: HTMLImageElement): boolean {
  return img.dataset.altState === 'pending';
}

/** Один глобальний обробник помилок завантаження <img>: перебирає запасні адреси по черзі. */
export function installImageFallback(): void {
  window.addEventListener(
    'error',
    (e) => {
      const img = e.target;
      if (!(img instanceof HTMLImageElement) || img.dataset.altState === 'done') return;
      const orig = img.dataset.altOrig ?? (img.currentSrc || img.src);
      const candidates = imageUrlCandidates(orig);
      const next = Number(img.dataset.altIdx ?? -1) + 1;
      if (next >= candidates.length) {
        // варіанти вичерпано (або їх не було) — далі діє звичайна обробка помилки
        if (img.dataset.altOrig) img.dataset.altState = 'done';
        return;
      }
      img.dataset.altOrig = orig;
      img.dataset.altIdx = String(next);
      img.dataset.altState = 'pending'; // власні обробники error цієї картинки, що йдуть за нами, це бачать
      img.src = candidates[next];
    },
    true,
  );
  // успішне завантаження знімає позначку "спроба триває"
  window.addEventListener(
    'load',
    (e) => {
      const img = e.target;
      if (img instanceof HTMLImageElement && img.dataset.altState === 'pending') img.dataset.altState = 'ok';
    },
    true,
  );
}

/**
 * HoneyManga: CDN обкладинок (Bunny) відповідає 403 на запити з чужим Referer
 * (у нас — адреса читалки/ngrok), але пускає запити без Referer; сам сайт
 * просить обкладинки як `?optimizer=image&width=512`. Тому для цього хоста:
 *  - normalizeCoverUrl додає ці параметри, якщо їх немає (старі збережені
 *    посилання бувають без них);
 *  - isNoReferrerHost каже, що <img> / Image для нього треба створювати з
 *    referrerPolicy = 'no-referrer'.
 */
const NO_REFERRER_HOSTS = new Set(['hmvolumestorage.b-cdn.net']);

function hostOf(src: string): string | null {
  try {
    return new URL(src, window.location.href).hostname.toLowerCase();
  } catch {
    return null;
  }
}

export function isNoReferrerHost(src: string): boolean {
  const h = hostOf(src);
  return !!h && NO_REFERRER_HOSTS.has(h);
}

export function normalizeCoverUrl(src: string): string {
  if (!isNoReferrerHost(src)) return src;
  try {
    const url = new URL(src, window.location.href);
    if (!url.searchParams.has('optimizer')) url.searchParams.set('optimizer', 'image');
    if (!url.searchParams.has('width')) url.searchParams.set('width', '512');
    return url.toString();
  } catch {
    return src;
  }
}

function probe(url: string, timeoutMs = 15000): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    const timer = window.setTimeout(() => done(false), timeoutMs);
    function done(ok: boolean) {
      window.clearTimeout(timer);
      img.onload = img.onerror = null;
      resolve(ok);
    }
    img.onload = () => done(true);
    img.onerror = () => done(false);
    if (isNoReferrerHost(url)) img.referrerPolicy = 'no-referrer';
    img.src = url;
  });
}

/** Перша адреса з (оригінал + запасні), яка справді завантажується як картинка; null — жодна. */
export async function resolveWorkingImageUrl(src: string): Promise<string | null> {
  for (const url of [src, ...imageUrlCandidates(src)]) {
    if (await probe(url)) return url;
  }
  return null;
}

function cssUrl(u: string): string {
  return `url("${u.replace(/["\\\n]/g, encodeURIComponent)}")`;
}

/**
 * Фон-обкладинка шапки (CSS background — події помилки в нього немає, тому
 * <img>-обробник тут не спрацює): одразу ставимо оригінал, паралельно
 * перевіряємо, чи він завантажується, і за потреби підставляємо робочий
 * варіант; якщо не завантажується жоден — градієнт (fallbackGradient).
 */
export function setHeroBackground(el: HTMLElement, cover: string, fallbackGradient: string): void {
  cover = normalizeCoverUrl(cover);
  el.style.backgroundImage = cssUrl(cover);
  void resolveWorkingImageUrl(cover).then((working) => {
    if (working === null) el.style.backgroundImage = fallbackGradient;
    else if (working !== cover) el.style.backgroundImage = cssUrl(working);
  });
}
